import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle, Building2, CheckCircle, Pencil, Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { formatDateTime } from '../utils/datetime';
import SearchableSelect from '../components/ui/SearchableSelect';
import { isMerchantReady } from '../utils/cashfree';

const emptyForm = {
  name: '', email: '', phone: '', accountType: 'BUSINESS', businessType: 'Education', pan: '', gst: '',
  payout: 'BANK', accountHolder: '', accountNumber: '', confirmAccountNumber: '', ifsc: '', upi: '',
  onCashfree: true, onPayu: false, payuEntityId: '', payuCategory: '44:388', monthlyVolume: '',
};

// PayU business entity types (business_entity_id); blank lets the server guess from the account type.
const PAYU_ENTITIES = [
  [14, 'Individual'], [2, 'Sole Proprietorship'], [3, 'Partnership'], [4, 'Private Limited'], [5, 'Public Limited'],
  [6, 'LLP'], [16, 'One Person Company'], [7, 'Trust'], [8, 'Society'], [15, 'NGO'], [13, 'Education'],
  [1, 'Association of Persons'], [10, 'Hindu Undivided Family'], [9, 'Government'], [11, 'Local Authority'],
  [12, 'Non Individual'], [17, 'Artificial Juridical Person'],
];

// PayU business category:sub-category ids that fit event organisers.
const PAYU_CATEGORIES = [
  ['44:388', 'Events & Conferences'],
  ['17:316', 'Education: Colleges & Universities'],
  ['17:317', 'Education: Other educational services'],
  ['45:398', 'Healthcare: Other healthcare services'],
  ['33:321', 'Membership organisations'],
  ['33:318', 'NGO'],
];

const Field = ({ id, label, hint, children }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

/** linking: { merchant, provider } when adding an existing merchant to a gateway it isn't on yet. */
const MerchantForm = ({ initial, editing, linking, payuOnboarding, onCancel, onSaved }) => {
  const [form, setForm] = useState(initial);
  const { data: businessTypes = [] } = useQuery({
    queryKey: ['merchantBusinessTypes'],
    queryFn: () => ApiService.getMerchantBusinessTypes(),
    staleTime: Infinity,
  });
  const { showSuccess, showError } = useToast();
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const save = useMutation({
    mutationFn: (payload) => {
      if (linking) return ApiService.linkPaymentMerchant(linking.merchant.id, linking.provider, payload);
      return editing ? ApiService.updatePaymentMerchant(editing.id, payload) : ApiService.createPaymentMerchant(payload);
    },
    onSuccess: (merchant) => {
      if (linking) {
        showSuccess(`${merchant.name} added to ${linking.provider === 'PAYU' ? 'PayU' : 'Cashfree'}.`);
      } else if (editing) {
        showSuccess('Merchant updated');
      } else {
        const notes = [
          merchant.onCashfree !== false && 'Cashfree verifies the bank account (minutes in sandbox, about a day live)',
          merchant.payuMid && `PayU child merchant ${merchant.payuMid} created; finish documents in the PayU dashboard`,
          merchant.payuError && `PayU: ${merchant.payuError}`,
        ].filter(Boolean);
        (merchant.payuError ? showError : showSuccess)(`${merchant.name} added.${notes.length ? ` ${notes.join('. ')}.` : ''}`);
      }
      onSaved();
    },
    onError: (err) => showError(err?.message || 'Could not save the merchant'),
  });

  const submit = (e) => {
    e.preventDefault();
    const bank = form.payout === 'BANK';
    if (bank && form.accountNumber && form.accountNumber !== form.confirmAccountNumber) {
      showError('Account numbers do not match');
      return;
    }
    const wantsPayu = linking ? linking.provider === 'PAYU' : form.onPayu;
    if (!editing && !linking && !form.onCashfree && !form.onPayu
      && !window.confirm('Add this merchant without a payment gateway? It can only be paid manually from "collect, transfer later" events.')) {
      return;
    }
    const [payuCategoryId, payuSubCategoryId] = (form.payuCategory || '44:388').split(':').map(Number);
    save.mutate({
      providers: editing || linking ? undefined : [form.onCashfree && 'CASHFREE', form.onPayu && 'PAYU'].filter(Boolean),
      payuEntityId: wantsPayu && form.payuEntityId ? Number(form.payuEntityId) : null,
      payuCategoryId: wantsPayu ? payuCategoryId : null,
      payuSubCategoryId: wantsPayu ? payuSubCategoryId : null,
      monthlyVolume: wantsPayu && form.monthlyVolume ? Number(form.monthlyVolume) : null,
      name: form.name,
      email: form.email,
      phone: form.phone,
      accountType: form.accountType,
      businessType: form.businessType,
      pan: form.pan || null,
      gst: form.gst || null,
      accountHolder: form.accountHolder || null,
      accountNumber: bank ? form.accountNumber || null : null,
      ifsc: bank ? form.ifsc || null : null,
      upi: bank ? null : form.upi || null,
    });
  };

  const showPayu = linking ? linking.provider === 'PAYU' : !editing && form.onPayu;
  return (
    <form onSubmit={submit} className="space-y-6">
      {linking && (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-3">
          Creating {linking.merchant.name} on {linking.provider === 'PAYU' ? 'PayU' : 'Cashfree'}. Re-enter the bank account number: only its last 4 digits are stored here.
        </p>
      )}
      {!editing && !linking && (
        <div>
          <h3 className="font-medium text-gray-900 mb-2">Create this merchant on</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.onCashfree} onChange={(e) => setForm((p) => ({ ...p, onCashfree: e.target.checked }))} />
              Cashfree (Easy Split vendor)
            </label>
            <label className={`flex items-center gap-2 ${payuOnboarding ? '' : 'text-gray-400'}`}>
              <input type="checkbox" checked={form.onPayu} disabled={!payuOnboarding}
                onChange={(e) => setForm((p) => ({ ...p, onPayu: e.target.checked }))} />
              PayU (child merchant)
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Leave a gateway unticked to add it later from the merchant card. With neither, the merchant can only be paid manually.
            {!payuOnboarding && ' PayU onboarding by API needs EVENT_PAYU_CLIENT_ID, EVENT_PAYU_CLIENT_SECRET and EVENT_PAYU_PARENT_MID on the server; you can still add it in the PayU dashboard and paste its key.'}
          </p>
        </div>
      )}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">1. Organizer details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="m-name" label="Legal name *" hint="As on PAN / bank account. Letters, numbers, . & / - only.">
            <Input id="m-name" value={form.name} onChange={set('name')} required />
          </Field>
          <Field id="m-email" label="Email *"><Input id="m-email" type="email" value={form.email} onChange={set('email')} required /></Field>
          <Field id="m-phone" label="Mobile *"><Input id="m-phone" inputMode="tel" value={form.phone} onChange={set('phone')} required /></Field>
          <Field id="m-type" label="Account type">
            <select id="m-type" className="h-10 w-full rounded-md border border-gray-300 px-2 text-sm" value={form.accountType} onChange={set('accountType')}>
              <option value="BUSINESS">Business / institution</option>
              <option value="INDIVIDUAL">Individual</option>
            </select>
          </Field>
        </div>
      </div>
      <div>
        <h3 className="font-medium text-gray-900 mb-3">2. KYC</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field id="m-pan" label={editing ? 'PAN' : 'PAN *'}><Input id="m-pan" value={form.pan} onChange={set('pan')} placeholder="ABCDE1234F" required={!editing} /></Field>
          <Field id="m-gst" label="GSTIN (optional)"><Input id="m-gst" value={form.gst} onChange={set('gst')} /></Field>
          <Field id="m-btype" label="Business category *">
            <SearchableSelect id="m-btype" value={form.businessType} options={businessTypes}
              onChange={(value) => setForm((prev) => ({ ...prev, businessType: value }))} placeholder="Search categories" />
          </Field>
        </div>
      </div>
      <div>
        <h3 className="font-medium text-gray-900 mb-3">3. Where settlements go</h3>
        <div className="flex gap-4 mb-3 text-sm">
          {[['BANK', 'Bank account'], ['UPI', 'UPI ID']].map(([value, label]) => (
            <label key={value} className="flex items-center gap-2">
              <input type="radio" name="payout" value={value} checked={form.payout === value} onChange={set('payout')} /> {label}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="m-holder" label="Account holder name *"><Input id="m-holder" value={form.accountHolder} onChange={set('accountHolder')} required={!editing} /></Field>
          {form.payout === 'BANK' ? (
            <>
              <Field id="m-ifsc" label="IFSC *"><Input id="m-ifsc" value={form.ifsc} onChange={set('ifsc')} placeholder="HDFC0001234" required={!editing || !!form.accountNumber} /></Field>
              <Field id="m-acc" label="Account number *"><Input id="m-acc" inputMode="numeric" value={form.accountNumber} onChange={set('accountNumber')} required={!editing} /></Field>
              <Field id="m-acc2" label="Confirm account number *"><Input id="m-acc2" inputMode="numeric" value={form.confirmAccountNumber} onChange={set('confirmAccountNumber')} required={!editing || !!form.accountNumber} /></Field>
            </>
          ) : (
            <Field id="m-upi" label="UPI ID *" hint="Works only after Cashfree enables UPI settlements for vendors on the platform account; otherwise use a bank account.">
              <Input id="m-upi" value={form.upi} onChange={set('upi')} placeholder="name@bank" required={!editing} />
            </Field>
          )}
        </div>
        {editing && <p className="text-xs text-gray-500 mt-2">Leave bank fields empty to keep the current account (…{editing.accountNumberLast4 || '—'}).</p>}
      </div>
      {showPayu && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">4. PayU details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field id="m-payu-entity" label="Entity type" hint="Blank: guessed from the account type and category.">
              <select id="m-payu-entity" className="h-10 w-full rounded-md border border-gray-300 px-2 text-sm" value={form.payuEntityId} onChange={set('payuEntityId')}>
                <option value="">Choose automatically</option>
                {PAYU_ENTITIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </Field>
            <Field id="m-payu-cat" label="PayU business category">
              <select id="m-payu-cat" className="h-10 w-full rounded-md border border-gray-300 px-2 text-sm" value={form.payuCategory} onChange={set('payuCategory')}>
                {PAYU_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </Field>
            <Field id="m-payu-vol" label="Expected monthly volume (₹)">
              <Input id="m-payu-vol" type="number" min="0" value={form.monthlyVolume} onChange={set('monthlyVolume')} placeholder="100000" />
            </Field>
          </div>
          <p className="text-xs text-gray-500 mt-2">PayU settles to a bank account (UPI isn't accepted). After creation, upload the signed Schedule C and bank proof in PayU's dashboard; PayU activates the sub-merchant in about two working days.</p>
        </div>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : linking ? `Create on ${linking.provider === 'PAYU' ? 'PayU' : 'Cashfree'}` : editing ? 'Save changes' : 'Add merchant'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};

// What a Cashfree vendor status means and what to do about it.
const STATUS_HELP = {
  IN_BENE_CREATION: { tone: 'wait', text: 'Cashfree is verifying the payout account. This refreshes automatically every 30 minutes.' },
  BANK_VALIDATION_FAILED: {
    tone: 'fail',
    text: "Cashfree couldn't verify the bank account. Usually the account number or IFSC is wrong, the account is closed or dormant, or the account holder name doesn't match the bank's records. Edit the merchant with the correct details (holder name exactly as on the passbook); Cashfree re-verifies after you save.",
  },
  BLOCKED: { tone: 'fail', text: 'Cashfree has blocked this vendor, so no payouts go to it. Contact Cashfree support with the vendor ID.' },
  DELETED: { tone: 'fail', text: 'This vendor was deleted in Cashfree. Add the merchant again.' },
};

const DOC_BLOCKED = /ACTION_REQUIRED|REJECTED|INVALID|FAILED/i;

const statusHelp = (merchant) => {
  const blockedDocs = (merchant.documentIssues || []).filter((issue) => DOC_BLOCKED.test(issue));
  if (blockedDocs.length && !isMerchantReady(merchant)) {
    return {
      tone: 'fail',
      text: 'Cashfree needs a KYC detail corrected before it can activate this merchant, most often the PAN (it must be valid and match the name and account type; in sandbox use a test PAN such as ABCPV1234D for an individual). Use “Fix details” to correct it.',
    };
  }
  return defaultHelp(merchant);
};

const defaultHelp = (merchant) => STATUS_HELP[(merchant.cashfreeStatus || '').toUpperCase()]
  || (merchant.cashfreeStatus && !isMerchantReady(merchant)
    ? { tone: 'fail', text: `Cashfree reports status ${merchant.cashfreeStatus}. Payments can't be settled to this merchant until it is ACTIVE.` }
    : null);

const StatusBadge = ({ merchant }) => {
  if (merchant.enabled === false) return <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">Disabled</span>;
  if (merchant.onCashfree === false) return <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">Not on Cashfree</span>;
  if (isMerchantReady(merchant)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
        <ShieldCheck className="w-3 h-3" /> Ready for payments
      </span>
    );
  }
  const failed = statusHelp(merchant)?.tone === 'fail';
  return (
    <span className={`text-xs font-medium px-2 py-1 rounded ${failed ? 'text-red-700 bg-red-100' : 'text-amber-700 bg-amber-100'}`}>
      {(merchant.cashfreeStatus || 'Pending').replace(/_/g, ' ')}
    </span>
  );
};

/** PayU onboards child merchants on its side; paste the child key it gives to let PayU pay this merchant. */
const PayuKeyField = ({ merchant, onSaved }) => {
  const { showSuccess, showError } = useToast();
  const [value, setValue] = useState(merchant.payuChildKey || '');
  const [saving, setSaving] = useState(false);
  const changed = value.trim() !== (merchant.payuChildKey || '');
  const save = async () => {
    setSaving(true);
    try {
      await ApiService.setMerchantPayuKey(merchant.id, value.trim());
      onSaved();
      showSuccess(value.trim() ? 'PayU child key saved' : 'PayU child key removed');
    } catch (err) {
      showError(err?.message || 'Could not save the PayU key');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-gray-600">PayU child key</span>
      <input className="h-7 w-40 rounded border border-gray-300 px-2 font-mono" value={value} maxLength={32}
        placeholder="not set" onChange={(e) => setValue(e.target.value)} aria-label={`PayU child merchant key for ${merchant.name}`} />
      {changed && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>}
      {merchant.payuChildKey && !changed && <span className="text-green-700">PayU ready</span>}
    </div>
  );
};

const PaymentMerchantsPage = () => {
  const [mode, setMode] = useState(null);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const refreshList = () => queryClient.invalidateQueries({ queryKey: ['paymentMerchants'] });

  const { data: platform } = useQuery({ queryKey: ['paymentPlatform'], queryFn: () => ApiService.getPaymentPlatformStatus() });
  const { data: merchants = [], isLoading } = useQuery({ queryKey: ['paymentMerchants'], queryFn: () => ApiService.listPaymentMerchants() });

  const refresh = useMutation({
    mutationFn: (id) => ApiService.refreshPaymentMerchant(id),
    onSuccess: (m) => { refreshList(); showSuccess(`Cashfree status: ${m.cashfreeStatus}`); },
    onError: (err) => showError(err?.message || 'Could not refresh'),
  });
  const remove = useMutation({
    mutationFn: (m) => ApiService.deletePaymentMerchant(m.id),
    onSuccess: () => { refreshList(); showSuccess('Merchant deleted'); },
    onError: (err) => showError(err?.message || 'Could not delete the merchant'),
  });
  const toggle = useMutation({
    mutationFn: (m) => ApiService.updatePaymentMerchant(m.id, { enabled: m.enabled === false }),
    onSuccess: refreshList,
    onError: (err) => showError(err?.message || 'Could not update'),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment merchants</h1>
          <p className="text-gray-600 mt-1">Organizers who receive online payments directly. Events can also collect on the platform account.</p>
        </div>
        {!mode && <Button onClick={() => setMode({ type: 'create' })}><Plus className="w-4 h-4 mr-2" /> Add merchant</Button>}
      </div>

      <Card>
        <CardContent className="p-5 flex flex-wrap items-center gap-3 text-sm">
          {platform?.configured ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium">Platform Cashfree account (default)</div>
            <div className="text-gray-600">
              {platform?.configured
                ? `Connected${platform.sandbox ? ' · sandbox (test payments only)' : ' · live'}${platform.webhook ? ' · webhook on' : ' · webhook URL not set, payments confirm via status checks'}`
                : 'Not connected. Set EVENT_CASHFREE_APP_ID and EVENT_CASHFREE_SECRET_KEY on the server to take online payments.'}
            </div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium">Platform PayU account</div>
            <div className="text-gray-600">
              {platform?.payu?.configured
                ? `Connected${platform.payu.test ? ' · test mode' : ' · live'}${platform.payu.onboarding ? ' · can create child merchants' : ' · child merchants: add in PayU dashboard'}`
                : 'Not connected. Set EVENT_PAYU_KEY and EVENT_PAYU_SALT on the server.'}
            </div>
          </div>
        </CardContent>
      </Card>

      {mode && (
        <Card>
          <CardHeader>
            <CardTitle>
              {mode.type === 'edit' ? `Edit ${mode.merchant.name}`
                : mode.type === 'link' ? `Add ${mode.merchant.name} to ${mode.provider === 'PAYU' ? 'PayU' : 'Cashfree'}` : 'Add merchant'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MerchantForm
              key={`${mode.type}-${mode.merchant?.id || 'new'}-${mode.provider || ''}`}
              linking={mode.type === 'link' ? { merchant: mode.merchant, provider: mode.provider } : null}
              payuOnboarding={!!platform?.payu?.onboarding}
              initial={mode.type === 'edit' || mode.type === 'link' ? { ...emptyForm, ...mode.merchant, pan: mode.merchant.pan || '', gst: mode.merchant.gst || '', payout: mode.merchant.upi && !mode.merchant.accountNumberLast4 ? 'UPI' : 'BANK', accountNumber: '', confirmAccountNumber: '', upi: mode.merchant.upi || '' } : emptyForm}
              editing={mode.type === 'edit' ? mode.merchant : null}
              onCancel={() => setMode(null)}
              onSaved={() => { setMode(null); refreshList(); }}
            />
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
      ) : merchants.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            No merchants yet. Events collect on the platform account until you add one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {merchants.map((m) => (
            <Card key={m.id}>
              <CardContent className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{m.name}</h3>
                    <StatusBadge merchant={m} />
                  </div>
                  <p className="text-sm text-gray-600">{m.email} · {m.phone}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Vendor <span className="font-mono">{m.vendorId}</span>
                    {m.accountNumberLast4 && <> · A/c …{m.accountNumberLast4} ({m.ifsc})</>}
                    {m.upi && <> · UPI {m.upi}</>}
                    {m.settlementSchedule && <> · {m.settlementSchedule}</>}
                  </p>
                  {m.lastError && <p className="text-xs text-red-600 mt-1">{m.lastError}</p>}
                  <PayuKeyField merchant={m} onSaved={refreshList} />
                  {m.payuMid && <p className="text-xs text-gray-500 mt-1">PayU child merchant {m.payuMid}{m.payuStatus ? ` · ${m.payuStatus}` : ''}</p>}
                  {m.payuError && <p className="text-xs text-red-600 mt-1">PayU: {m.payuError}</p>}
                  {!isMerchantReady(m) && m.enabled !== false && m.onCashfree !== false && (() => {
                    const help = statusHelp(m);
                    const failed = help?.tone === 'fail';
                    return (
                      <div className={`mt-2 rounded border p-2 text-xs ${failed ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                        {m.remarks && <p className="font-medium">Cashfree says: {m.remarks}</p>}
                        {help && <p className={m.remarks ? 'mt-1' : ''}>{help.text}</p>}
                        {m.documentIssues?.length > 0 && (
                          <ul className="mt-1 list-disc pl-4">
                            {m.documentIssues.map((issue) => <li key={issue}>{issue.replace(/_/g, ' ')}</li>)}
                          </ul>
                        )}
                        {failed && (
                          <Button size="sm" variant="outline" className="mt-2 h-7" onClick={() => setMode({ type: 'edit', merchant: m })}>
                            Fix details
                          </Button>
                        )}
                      </div>
                    );
                  })()}
                  {m.statusCheckedAt && <p className="text-xs text-gray-400 mt-1">Checked {formatDateTime(m.statusCheckedAt)}</p>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {m.onCashfree === false ? (
                    <Button size="sm" variant="outline" onClick={() => setMode({ type: 'link', provider: 'CASHFREE', merchant: m })}>
                      <Plus className="w-4 h-4 mr-1" /> Add to Cashfree
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => refresh.mutate(m.id)} disabled={refresh.isPending}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  )}
                  {!m.payuMid && platform?.payu?.onboarding && (
                    <Button size="sm" variant="outline" onClick={() => setMode({ type: 'link', provider: 'PAYU', merchant: m })}>
                      <Plus className="w-4 h-4 mr-1" /> Add to PayU
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setMode({ type: 'edit', merchant: m })}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle.mutate(m)}>{m.enabled === false ? 'Enable' : 'Disable'}</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" disabled={remove.isPending}
                    onClick={() => window.confirm(`Delete ${m.name}? Its Cashfree vendor is deleted too and it can't be selected for events any more.`) && remove.mutate(m)}>
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentMerchantsPage;
